import { BoxPanel } from "screens/styles";
import { FormHelperText, Stack, Typography, useTheme, Checkbox, FormControl, FormControlLabel, FormGroup, FormLabel } from "@mui/material";
import { QuestionFormRoot, StyledAnswerArea, StyledSecondaryButton, ClearButtonWrapper, StyledFormControlLabel, StyledIconTextWrapper, StyledIcon, StyledText } from "./style";
import React, { forwardRef, useEffect, useMemo, useRef, useState } from "react";
import { urlConverter } from "utils";
import { interpolate } from "components/core";
import { extractQuestions } from "utils/src/extractQuestions";
import { useOnlineBookingApi } from "shell";
import { handleHttpCode, questionTypeEnum } from "assets/constants";
import { updateQuestionAnswerDict } from "utils/src/updateQuestionAnswerDict";
import { qnTexts } from "shell/OnlineBookingTheme/themes/_palette";
import { Box } from "@mui/system";
import { QuestionMultiSkeleton } from "./Skeleton/QuestionMultiSkeleton";
import WarningCreature from "assets/icons/creatures/Warning";
import { useDispatch } from "react-redux";
import { resetUserDataNextSteps, resetUserDataNextWidgets, userDataSet } from "store/userData";
import { questionMaper } from "./utils";

const requiredAddon = () => <span style={{ color: 'var(--mui-palette-red-900)' }}> *</span>;

const MultiQuestion = forwardRef(({ data, questions, setAnswer, userData, isMultiSelection, isFirstRender, activeStep, propertyId, properties }, ref) => {
    const theme = useTheme();
    const api = useOnlineBookingApi();
    const dispatch = useDispatch();

    let { questionType,
        questionConfig,
        answersConfig,
        questionKey,
        title,
        additionalDescription,
        answers,
        buttonClear,
        isMandatory,
        question,
        executeAfterProxyAnswer,
        executeAfterInternalAnswer,
    } = data || {};
    const isMapToObject = questionConfig?.mapToObjectType === 5
    const errorRef = useRef(null);

    const [selectedValues, setSelectedValues] = useState(
        questions && questions[questionKey]
        ? questions[questionKey].split("[OBsep]").map((guid) => guid.trim())
        : []
    ); // State for multi-selection
    const [multiselectionError, setMultiselectionError] = useState(false); // State for multi-selection error
    const [skipReset, setSkipReset] = useState(false);

    const { customerGUID, clientStateData, journeyGUID } = useMemo(() => {
        return {
            customerGUID: userData.customerGUID,
            clientStateData: userData.clientStateData,
            journeyGUID: userData.journeyGUID
        };
    }, [userData]);

    const questionJsonData = useMemo(() => JSON.stringify(questionConfig), [questionConfig]);


    const currentAnswersArray = useMemo(() => {
        if (!isMultiSelection) return;
        if (!questions || !questionKey) return [];
        return (
            questions[questionKey]?.split("[OBsep]").map((guid) => guid.trim()) || []
        );
    }, [questions, questionKey, isMultiSelection]);

    const questionAnswerDict = extractQuestions(userData)
    const questionAnswerDictStible = useMemo(() => questionAnswerDict, [questionAnswerDict?.length]);
    const isUserDataLoaded = !!((clientStateData && clientStateData?.customerGUID && clientStateData?.journeyGUID) || (userData && userData.journeyGUID));

    const { isLoading: isQuestionsLoading, data: questionsData, error: webHooQuestionkError, refetch } = api.questions.useQuestionQuery(
        clientStateData?.customerGUID || customerGUID,
        clientStateData?.journeyGUID || journeyGUID,
        questionAnswerDictStible,
        questionType || questionTypeEnum.multiSelection,
        questionJsonData || null,
        propertyId,
        clientStateData?.serviceId || userData?.service?.id,
        clientStateData?.serviceTypeId || userData?.serviceType?.id,
        clientStateData?.unitId || userData?.location?.id,
        clientStateData?.appointmentTypeId || userData?.appointmentType?.id,
        clientStateData?.calendarIds,
        clientStateData?.selectedSlot,
        clientStateData?.selectedDate,
        isUserDataLoaded,
        userData
    )
    const { mutate: answersMutate, isLoading: isAnsweresLoading, error: answerError, reset: resetAnswer } = api.answers.useAnswerMutation();

    const showQuestion = !!(!isQuestionsLoading && questionsData?.showQuestion);
    const parsedData = !!questionsData?.jsonData && questionMaper(JSON.parse(questionsData?.jsonData))
    if (parsedData) {
        questionConfig = parsedData?.questionConfig
        answersConfig = parsedData?.answersConfig
        questionKey = parsedData?.questionKey
        title = parsedData?.title
        additionalDescription = parsedData?.additionalDescription
        answers = parsedData?.answers
        buttonClear = parsedData?.buttonClear
        isMandatory = parsedData?.isMandatory
        question = parsedData?.question
        executeAfterProxyAnswer = parsedData?.executeAfterProxyAnswer
        executeAfterInternalAnswer = parsedData?.executeAfterInternalAnswer
    }


    useEffect(() => {
        if (answerError && errorRef.current) {
            errorRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
            errorRef.current.focus();
        }

    }, [answerError]);



    /*** Whenever `selectedValues` changes in multi-selection mode,* join them into a single string and call onChange.*/
    useEffect(() => {
        if (isMultiSelection && selectedValues) {
            setSkipReset(true);
        }
        setSkipReset(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedValues, isMultiSelection]);


    useEffect(() => {
        const refetchThisQuestionAfterCurrent = userData[`after_property_questions_on_step_${activeStep}`]?.some(item => item?.propertyId === propertyId)
        if (refetchThisQuestionAfterCurrent && !isQuestionsLoading) {
            refetch()
            resetAnswer()
            setSelectedValues([])
        }
    }, [userData[`after_property_questions_on_step_${activeStep}`]]);


    /*** If the question is invalid and loading is done, set answer as "" with current question key, for set appointmen validation on submit. */
    useEffect(() => {
        if (!showQuestion && !isQuestionsLoading && !webHooQuestionkError) {
            setAnswer({ ...questions, [questionKey]: "" });
        }
    }, [showQuestion, isQuestionsLoading]);

    /* If there's an error in answering, remove this question's entry from `questions`.   */
    useEffect(() => {
        if (!isAnsweresLoading && answerError) {
            const { [questionKey]: removedProperty, ...restOfQuestions } = questions;
            setAnswer({ ...restOfQuestions });
        }
    }, [answerError, isQuestionsLoading]);

    /*** Handle toggling an answer in multi-select mode.*/
    const onCheckboxSelect = (answerKey) => {
        if (!isMultiSelection) return;
        setMultiselectionError(false);

        setSelectedValues((prevSelected) => {
            const current = Array.isArray(prevSelected) ? prevSelected : [];
            const updatedValues = current.includes(answerKey)
                ? current.filter((key) => key !== answerKey)
                : [...current, answerKey];

            // Check if the updatedValues array is empty and the question is mandatory
            if (isMandatory && updatedValues.length === 0) {
                setMultiselectionError(true);
                return current
            }
            onChange((updatedValues|| []).join("[OBsep]"));
            return updatedValues;
        });
        setTimeout(() => {
            dispatch(resetUserDataNextSteps());
        });
    };


    const selectedValue = (!!questions && questions[questionKey]) || '';
    const hasAnswerWebhook = executeAfterProxyAnswer || executeAfterInternalAnswer;

    const onChange = (answerKey) => {
        const selectedKeys = answerKey.includes('[OBsep]') ? answerKey.split('[OBsep]') : [answerKey]
        const selectedAnswerConfigs = selectedKeys.reduce((acc, key) => {
            const match = answersConfig?.find(answer => answer.answerKey === key);
            if (match) acc.push(match);
            return acc;
        }, []);
        const answersJsonData = selectedAnswerConfigs.length > 1
            ? JSON.stringify(selectedAnswerConfigs)
            : JSON.stringify(selectedAnswerConfigs[0]);
        const emptySelect = answerKey === '';
        const noQuestionsAndEmptySelect = !questions && emptySelect;

        if (noQuestionsAndEmptySelect) return;
        const { updatedQuestionAnswerDict, updatedClientStateData } = updateQuestionAnswerDict(clientStateData, questionAnswerDict, questionKey, answerKey)
        if (!!questions) {
            const noPreviousSelectAndEmptySelect = !questions[questionKey] && emptySelect
            const previousSelectOrEmptySelect = (questions[questionKey] === answerKey || emptySelect) && ((!isMandatory || !data?.questionConfig?.isMandatory) || isMultiSelection);
            if (noPreviousSelectAndEmptySelect) return;
            if (previousSelectOrEmptySelect) {
                const { [questionKey]: removedProperty, ...restOfQuestions } = questions;
                setAnswer({ ...restOfQuestions })

                setTimeout(() => {
                    dispatch(userDataSet({
                        clientStateData: { questionAnswerDict: { ...restOfQuestions } }
                    }))
                })


                setTimeout(() => {
                    dispatch(resetUserDataNextWidgets({ properties, propertyId }));
                });

                setTimeout(() => {
                    dispatch(resetUserDataNextSteps());
                }, 30);

                if (!hasAnswerWebhook || !answersJsonData) return;
                answersMutate(
                    {// TODO RC: validate Answere check
                        customerGUID: clientStateData?.customerGUID || customerGUID,
                        journeyGUID: clientStateData?.journeyGUID || journeyGUID,
                        questionAnswerDict: (!!clientStateData?.questionAnswerDict && updatedClientStateData.questionAnswerDict) || updatedQuestionAnswerDict,
                        questionType: questionType || questionTypeEnum.multiSelection,
                        jsonData: answersJsonData,
                        propertyId: propertyId,
                        serviceId: clientStateData?.serviceId || userData?.service?.id,
                        serviceTypeId: clientStateData?.serviceTypeId || userData?.serviceType?.id,
                        unitId: clientStateData?.unitId || userData?.location?.id,
                        appointmentTypeId: clientStateData?.appointmentTypeId || userData?.appointmentType?.id,
                        calendarIds: clientStateData?.calendarIds,
                        selectedSlot: clientStateData?.selectedSlot,
                        selectedDate: clientStateData?.selectedDate,

                    }
                );
            }
        }
        if (hasAnswerWebhook && answersJsonData) {
            answersMutate(
                {// TODO RC: validate Answere check
                    customerGUID: clientStateData?.customerGUID || customerGUID,
                    journeyGUID: clientStateData?.journeyGUID || journeyGUID,
                    questionAnswerDict: (!!clientStateData?.questionAnswerDict && updatedClientStateData.questionAnswerDict) || updatedQuestionAnswerDict,
                    questionType: questionType || questionTypeEnum.multiSelection,
                    jsonData: answersJsonData,
                    propertyId: propertyId,
                    serviceId: clientStateData?.serviceId || userData?.service?.id,
                    serviceTypeId: clientStateData?.serviceTypeId || userData?.serviceType?.id,
                    unitId: clientStateData?.unitId || userData?.location?.id,
                    appointmentTypeId: clientStateData?.appointmentTypeId || userData?.appointmentType?.id,
                    calendarIds: clientStateData?.calendarIds,
                    selectedSlot: clientStateData?.selectedSlot,
                    selectedDate: clientStateData?.selectedDate,
                }
            );
        }

        if (isMapToObject) {
            setTimeout(() => {
                dispatch(userDataSet({ [`questions_with_filter`]: { ...userData.questions_with_filter || {}, [questionKey]: answerKey } }))
            })
        }

        setAnswer({ ...questions, [questionKey]: answerKey })

        setTimeout(() => {
            dispatch(resetUserDataNextWidgets({ properties, propertyId }));
        });

        if (skipReset) {
            setTimeout(() => {
                dispatch(resetUserDataNextSteps());
            });
        }
    }



    if (isQuestionsLoading) { return <QuestionMultiSkeleton /> }

    if (webHooQuestionkError) {
        return (
            <>
                <QuestionFormRoot
                    style={
                        {
                            outline: '1px solid',
                            outlineColor: theme.palette.red[700],
                            borderRadius: '10px',
                        }
                    }
                    ref={ref}>
                    <BoxPanel className="BoxShadow">
                        <StyledIconTextWrapper>

                            <StyledIcon>
                                <WarningCreature />
                            </StyledIcon>
                            <StyledText>
                                {
                                    webHooQuestionkError && (
                                        webHooQuestionkError?.errorCode === handleHttpCode.webHook_Error
                                            ? webHooQuestionkError?.webhookResults?.messages
                                                .map((msg, index) => (
                                                    <div key={index}>
                                                        <span style={{ color: theme.palette.red[700] }} > {msg.message}</span>
                                                    </div>
                                                ))
                                            : <span style={{ color: theme.palette.red[700] }} > {webHooQuestionkError?.title || 'General error'} </span>
                                    )
                                }
                            </StyledText>
                        </StyledIconTextWrapper>
                    </BoxPanel>
                </QuestionFormRoot>
            </>
        )
    }


    if (!isQuestionsLoading && !showQuestion) return null

    return (
        <>
            <QuestionFormRoot
                style={answerError || (multiselectionError && isMandatory)
                    ? {
                        outline: '1px solid',
                        outlineColor: theme.palette.red[700],
                        borderRadius: '10px',
                    } : {}
                }
                ref={ref}>
                <BoxPanel className="BoxShadow">
                    <Stack direction={'row'} justifyContent='space-between'>
                    <FormControl component="fieldset" variant="standard">
                        <FormLabel component="legend">
                            <Typography variant='h2' style={{ color: `${theme.vars.palette.primary.main}` }}>
                                {isMultiSelection ? title : data?.question?.title}{(isMandatory || data?.questionConfig?.isMandatory) && requiredAddon()}
                            </Typography>
                            <FormHelperText>
                                    {interpolate(additionalDescription || question?.additionalText || '', urlConverter, true)}
                            </FormHelperText>
                        </FormLabel>
                        <FormGroup>
                        {
                            answers.map(({ answerKey, title, additionalDescription }) =>
                                <FormControlLabel
                                    key={answerKey}
                                    control={
                                        <Checkbox
                                            checked={selectedValues?.includes(answerKey)}
                                            onChange={() => onCheckboxSelect(answerKey)}
                                        />}
                                    label={
                                        <Box sx={{ pointerEvents: 'none' }}>
                                            <FormHelperText style={{ color: qnTexts.darkGreyMediumEmphasis, fontSize: '14px' }}>
                                                {title}
                                            </FormHelperText>
                                            <FormHelperText>
                                                {additionalDescription}
                                            </FormHelperText>
                                        </Box>
                                    }
                                />
                            )
                        }
                        </FormGroup>
                    </FormControl>
                    {(!isMandatory && !data?.questionConfig?.isMandatory) &&
                        <ClearButtonWrapper>
                            <StyledSecondaryButton disabled={!selectedValues.length} onClick={() => {
                                setSelectedValues([])
                                onChange('')
                                resetAnswer()
                            }}>
                                {buttonClear || data.question.buttonClear}
                            </StyledSecondaryButton>
                        </ClearButtonWrapper>
                    }
                    </Stack>
                </BoxPanel>
            </QuestionFormRoot>

            {answerError && (
                <div ref={errorRef} tabIndex={-1}>
                    {
                        answerError?.errorCode === handleHttpCode.webHook_Error ?
                answerError?.webhookResults?.messages.map((msg, index) => (
                    <div key={index}>
                        <span style={{ color: theme.palette.red[700] }} > {msg.message}</span>
                    </div>
                )) : <span style={{ color: theme.palette.red[700] }} > {answerError?.title || 'General error'} </span>
                    }
                </div>
            )
            }
        </>
    )
});

export default MultiQuestion;